import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { updateGeneralSettings } from "../services/settings.services";
import { findSettingByType } from "../helpers/helpers";
import { addUser } from "../store/userSlice";
import { showSnackBar } from "../store/common/snackBarSlice";
import { useTranslation } from "react-i18next";

export const SETTING_KEYS = {
  systemTheme: "systemTheme",
  measurementUnit: "measurementUnit",
  // Needs the backend to accept it in general-setting/set-setting (see useLanguageSync).
  language: "language",
};

export const UNIT_SYSTEMS = ["Imperial", "Metric"];
export const DEFAULT_UNIT_SYSTEM = "Imperial";

export const useGeneralSettingValue = (settingType, fallback = null) => {
  const user = useSelector((state) => state.user);
  return (
    findSettingByType(user?.general_settings, settingType)?.value ?? fallback
  );
};

export const useSaveGeneralSetting = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const user = useSelector((state) => state.user);
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (settingType, value, { notify = true } = {}) => {
      setSaving(true);
      const response = await updateGeneralSettings({
        setting_type: settingType,
        value,
      });
      setSaving(false);

      if (!response?.data?.success) {
        if (notify) {
          dispatch(
            showSnackBar({
              text: t("messages.somethingWentWrong"),
              severity: "error",
            }),
          );
        }
        return false;
      }

      const updatedUser = { ...user, general_settings: response.data.result };
      dispatch(addUser(updatedUser));
      try {
        localStorage.setItem("user", JSON.stringify(updatedUser));
      } catch {
        // private mode — the Redux copy still holds for this session
      }

      if (notify) {
        dispatch(
          showSnackBar({
            text: t("messages.settingsUpdated"),
            severity: "success",
          }),
        );
      }
      return true;
    },
    [dispatch, user, t],
  );

  return { save, saving };
};

export const useUnitSystem = () => {
  const value = useGeneralSettingValue(
    SETTING_KEYS.measurementUnit,
    DEFAULT_UNIT_SYSTEM,
  );
  return UNIT_SYSTEMS.includes(value) ? value : DEFAULT_UNIT_SYSTEM;
};
